import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  useMediaQuery,
  useTheme,
  Avatar,
} from "@mui/material";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import GavelRoundedIcon from "@mui/icons-material/GavelRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import CreditScoreRoundedIcon from "@mui/icons-material/CreditScoreRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import SendToMobileRoundedIcon from "@mui/icons-material/SendToMobileRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import QrCode2RoundedIcon from "@mui/icons-material/QrCode2Rounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import { logoutApi, getMenuConfig } from "../api";

const DRAWER_W = 260;

const userMenu = [
  { label: "Dashboard", icon: <DashboardRoundedIcon />, path: "/dashboard" },
  { label: "My Profile", icon: <PersonRoundedIcon />, path: "/profile" },
  { label: "Add User", icon: <PersonAddAltRoundedIcon />, path: "/invite" },
  { label: "My Downline", icon: <AccountTreeRoundedIcon />, path: "/downline" },
  {
    label: "Wallet",
    icon: <AccountBalanceWalletRoundedIcon />,
    path: "/transactions",
  },
  { label: "Passbook", icon: <MenuBookRoundedIcon />, path: "/passbook" },
  { label: "Commissions", icon: <PaidRoundedIcon />, path: "/commissions" },
  { label: "Payins", icon: <CreditCardRoundedIcon />, path: "/payins" },
  { label: "POS & QR", icon: <QrCode2RoundedIcon />, path: "/pos-qr" },
  /* Not part of BiznessPay — payin (Pure Travel) + payout only.
  { label: "Pay Bills", icon: <ReceiptLongRoundedIcon />, path: "/pay-bills" },
  { label: "Card to Bank", icon: <SendToMobileRoundedIcon />, path: "/finkeda" },
  { label: "My Cards", icon: <CreditScoreRoundedIcon />, path: "/cards" },
  */
  { label: "Payouts", icon: <SavingsRoundedIcon />, path: "/payouts" },
];

const adminMenu = [
  {
    label: "Admin Dashboard",
    icon: <BarChartRoundedIcon />,
    path: "/admin/dashboard",
  },
  {
    label: "Reports",
    icon: <AssessmentRoundedIcon />,
    path: "/admin/reports",
  },
  { label: "All Users", icon: <PeopleAltRoundedIcon />, path: "/admin/users" },
  {
    label: "User Approvals",
    icon: <FactCheckRoundedIcon />,
    path: "/admin/approvals",
  },
  {
    label: "Commissions",
    icon: <PercentRoundedIcon />,
    path: "/admin/commissions",
  },
  {
    label: "Payout Approvals",
    icon: <GavelRoundedIcon />,
    path: "/admin/payouts",
  },
  {
    label: "POS & QR Approvals",
    icon: <PointOfSaleRoundedIcon />,
    path: "/admin/offline-collections",
  },
  {
    label: "QR & POS Setup",
    icon: <QrCode2RoundedIcon />,
    path: "/admin/payment-points",
  },
  {
    label: "Payout Charges",
    icon: <PaidRoundedIcon />,
    path: "/admin/payout-charges",
  },
  {
    label: "Payment Gateways",
    icon: <TuneRoundedIcon />,
    path: "/admin/gateways",
  },
  /* Not used in BiznessPay — uncomment any of these to re-enable it later
     (the pages, routes and backend all still exist).
  {
    label: "BBPS Billers",
    icon: <AccountBalanceRoundedIcon />,
    path: "/admin/billers",
  },
  {
    label: "Pending Bills",
    icon: <PendingActionsRoundedIcon />,
    path: "/admin/pending-bills",
  },
  {
    label: "Card to Bank Config",
    icon: <SendToMobileRoundedIcon />,
    path: "/finkeda/config",
  },
  */
  {
    label: "Menu Access",
    icon: <VisibilityRoundedIcon />,
    path: "/admin/menu-access",
  },
  {
    label: "Site Timing & Login",
    icon: <ScheduleRoundedIcon />,
    path: "/admin/site-timing",
  },
];

export default function Sidebar({ mobileOpen, onClose }) {
  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("md"));
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isAdmin = localStorage.getItem("roleName") === "ADMIN";
  const firstName = localStorage.getItem("firstName") || "User";
  const roleName = (localStorage.getItem("roleName") || "").replace(/_/g, " ");
  const roleLevel = Number(localStorage.getItem("roleLevel"));

  const [menuConfig, setMenuConfig] = useState(null);

  useEffect(() => {
    getMenuConfig()
      .then(({ data }) => setMenuConfig(data.menu || {}))
      .catch(() => setMenuConfig({}));
  }, []);

  /* Opt-in (test) items: hidden for everyone EXCEPT admins by default, and
     only shown to a role once it's explicitly granted in Menu Access. This is
     the inverse of the normal opt-out items (empty = visible to all). Used to
     roll out a feature to select roles without exposing it to everyone. */
  const OPT_IN_PATHS = ["/finkeda"];

  /* Show an item when: the config hasn't loaded yet; the item has no
     restriction; the user's role level is unknown; or the level is in the
     item's allowed list. Opt-in items flip the default to hidden. */
  const visibleUserMenu = userMenu.filter((item) => {
    if (OPT_IN_PATHS.includes(item.path)) {
      if (isAdmin) return true; // admins/testers always see it
      const allowed = menuConfig?.[item.path];
      return (
        Array.isArray(allowed) &&
        allowed.length > 0 &&
        !!roleLevel &&
        allowed.includes(roleLevel)
      );
    }
    /* Config governs everyone, including admins — admins can still reach
       the always-visible Admin section (incl. Menu Access) to change it,
       so they can never lock themselves out. */
    if (!menuConfig) return true; // not loaded yet — don't hide anything
    const allowed = menuConfig[item.path];
    if (!allowed || allowed.length === 0) return true; // unrestricted item
    if (!roleLevel) return true; // role level unknown — don't hide
    return allowed.includes(roleLevel);
  });

  const handleLogout = async () => {
    try {
      await logoutApi();
    } catch {
      /* ignore */
    }
    localStorage.clear();
    navigate("/signin");
  };

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <Toolbar sx={{ px: 2.5, gap: 1.25 }}>
        <Box
          component="img"
          src="/logo-icon.png"
          alt="BiznessPay"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
          sx={{ height: 48, width: 48, objectFit: "contain" }}
        />
        <Typography
          variant="h6"
          noWrap
          sx={{ fontWeight: 800, letterSpacing: -0.5 }}
        >
          <Box component="span" sx={{ color: "text.primary" }}>
            Bizness
          </Box>
          <Box component="span" sx={{ color: "primary.main" }}>
            Pay
          </Box>
        </Typography>
      </Toolbar>
      <Divider />

      <Box sx={{ px: 2, py: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            bgcolor: "primary.main",
            borderRadius: 3,
            color: "#fff",
          }}
        >
          <Avatar
            sx={{
              bgcolor: "rgba(255,255,255,0.25)",
              width: 36,
              height: 36,
              fontSize: 15,
              fontWeight: 700,
            }}
          >
            {firstName[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600} noWrap>
              {firstName}
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.8, textTransform: "capitalize" }}
            >
              {roleName.toLowerCase()}
            </Typography>
          </Box>
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 1.5, overflowY: "auto" }}>
        {visibleUserMenu.map((item) => (
          <ListItemButton
            key={item.path}
            selected={pathname === item.path}
            onClick={() => {
              navigate(item.path);
              if (!isMd) onClose();
            }}
            sx={{
              borderRadius: 2.5,
              mb: 0.5,
              py: 1,
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "#fff",
                "& .MuiListItemIcon-root": { color: "#fff" },
              },
              "&.Mui-selected:hover": { bgcolor: "primary.dark" },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 40,
                color: pathname === item.path ? "#fff" : "text.secondary",
              }}
            >
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                fontSize: 14,
                fontWeight: pathname === item.path ? 600 : 400,
              }}
            />
          </ListItemButton>
        ))}

        {isAdmin && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography
              variant="overline"
              sx={{ px: 2, color: "text.secondary", fontSize: 11 }}
            >
              Admin
            </Typography>
            {adminMenu.map((item) => (
              <ListItemButton
                key={item.path}
                selected={pathname === item.path}
                onClick={() => {
                  navigate(item.path);
                  if (!isMd) onClose();
                }}
                sx={{
                  borderRadius: 2.5,
                  mb: 0.5,
                  py: 1,
                  "&.Mui-selected": {
                    bgcolor: "primary.main",
                    color: "#fff",
                    "& .MuiListItemIcon-root": { color: "#fff" },
                  },
                  "&.Mui-selected:hover": { bgcolor: "primary.dark" },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: pathname === item.path ? "#fff" : "text.secondary",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14,
                    fontWeight: pathname === item.path ? 600 : 400,
                  }}
                />
              </ListItemButton>
            ))}
          </>
        )}
      </List>

      <Divider />
      <List sx={{ px: 1.5, pb: 1 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{ borderRadius: 2.5, py: 1 }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}>
            <LogoutRoundedIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{
              fontSize: 14,
              color: "error.main",
              fontWeight: 500,
            }}
          />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{ width: { md: DRAWER_W }, flexShrink: { md: 0 } }}
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DRAWER_W,
            bgcolor: "background.paper",
          },
        }}
      >
        {content}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            width: DRAWER_W,
            bgcolor: "background.paper",
          },
        }}
        open
      >
        {content}
      </Drawer>
    </Box>
  );
}
